import type { FunctionComponent } from 'react'
import React, { useContext as useContextCore } from 'react'
import type { Product } from '@ztechapps/common.contract'
import type { SaveCancelButtonsProps } from '@ztechapps/webui.button'
import { FormSaveCancelButtons } from '@ztechapps/webui.form'
import merge from 'lodash/merge'
import { AdjustmentAddFormInput } from './AdjustmentAddFormInput'
import { useAdjustmentAdd } from './useAdjustmentAdd'

export type AdjustmentAddFormValues = {
  product: Product
  quantity: number
}

export type AdjustmentAddComponent = {
  formInput: FunctionComponent
  saveCancelButtons: FunctionComponent<SaveCancelButtonsProps>
}

const defaultComponents: AdjustmentAddComponent = {
  formInput: AdjustmentAddFormInput,
  saveCancelButtons: FormSaveCancelButtons,
}

export type AdjustmentAddProviderProps = {
  onSubmit?: (values: AdjustmentAddFormValues) => Promise<void>
  onReset?: () => void
  children: React.ReactNode
  initialValues?: AdjustmentAddFormValues
  components?: AdjustmentAddComponent
}

export type AdjustmentAddContext = {
  validate: (value: AdjustmentAddFormValues) => any
} & Omit<AdjustmentAddProviderProps, 'children'>

const context = React.createContext<AdjustmentAddContext>({} as AdjustmentAddContext)
const CoreProvider = context.Provider

export const AdjustmentAddProvider = ({
  children,
  components,
  onSubmit,
  initialValues,
  ...props
}: AdjustmentAddProviderProps) => {
  initialValues = initialValues ?? { product: null, quantity: 1 }

  const { onSubmit: defaultOnSubmit } = useAdjustmentAdd()

  onSubmit = onSubmit ?? defaultOnSubmit

  //merge the components with the default components
  const mergedComponents = merge(defaultComponents, components)

  const validate = (value: AdjustmentAddFormValues) => {
    const errors: any = {}
    if (!value?.product?._id) {
      errors.product = 'Required'
    }
    if (!value.quantity) {
      errors.quantity = 'Cannot be set to zero'
    }

    return errors
  }

  return (
    <CoreProvider value={{ initialValues, validate, components: mergedComponents, onSubmit, ...props }}>
      {children}
    </CoreProvider>
  )
}

export const useAdjustmentAddContext = () => useContextCore(context)
